// Предохранитель по балансу — один на весь сайт (владелец 08.10: «защита должна стоять везде одинаковая и хорошо
// продумана»). Три рубежа у любой платной кнопки:
//   1) цена на кнопке — та же, что спишет сервер;
//   2) предпроверка до нажатия: useCreditsGuard (кнопка выключена, подсказка с суммами) + CreditsShortNote
//      (плашка «Не хватает N на этот запуск» с кнопкой «Пополнить»); цены «от» (итог по факту) — по нижней планке;
//   3) отказ сервера (402, код NO_TOKENS / NOT_ENOUGH_CREDITS / insufficient_credits или фраза «Недостаточно
//      токенов») api/client показывает одним тостом с «Пополнить» и перечитывает баланс (reportInsufficientCredits);
//      свой тост места вызова о том же гасится 3 секунды (insufficientReportedRecently), чтобы не было двух.
// Здесь — чистые функции без React, чтобы тесты шли без DOM.

export const INSUFFICIENT_CODES = new Set(['NO_TOKENS', 'NOT_ENOUGH_CREDITS', 'INSUFFICIENT_CREDITS', 'insufficient_credits'])
const PHRASES = ['недостаточно токенов', 'не хватает токенов', 'недостаточно кредитов', 'не хватает кредитов', 'insufficient', 'not enough credits', 'not enough tokens']
const NEEDED_KEYS = ['credits', 'required', 'needed', 'credits_required', 'needed_credits', 'total', 'price']
const BALANCE_KEYS = ['balance', 'available', 'credits_balance']

/** Токены без хвоста «.00»; дробные — с запятой, как в остальных подписях. */
export function fmtTokens(v: number): string {
	return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, '').replace('.', ',')
}

/** Цена известна, больше нуля и больше баланса. Бесплатное (0/null) никогда не «short». */
export function isShort(balance: number | null | undefined, price: number | null | undefined): boolean {
	const p = Number(price)
	return Number.isFinite(p) && p > 0 && Number(balance ?? 0) < p
}

/** Сколько не хватает до цены — та же арифметика, что в подсказке кнопки: баланс целыми (как «на балансе 33»). */
export function missingTokens(price: number, balance: number): number {
	return Math.max(0, Math.round((price - Math.floor(balance)) * 100) / 100)
}

/** Подсказка выключенной кнопки; from — цена «от» (итог по факту, проверяем нижнюю планку). */
export function shortTitle(price: number, balance: number, from = false): string {
	return `Не хватает токенов: нужно ${from ? 'от ' : ''}${fmtTokens(Math.round(price * 100) / 100)}, на балансе ${fmtTokens(Math.floor(balance))}`
}

export function isInsufficientMessage(text: unknown): boolean {
	const s = typeof text === 'string' ? text.toLowerCase() : ''
	return !!s && PHRASES.some((p) => s.includes(p))
}

type Bag = Record<string, unknown>
const bagOf = (v: unknown): Bag | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Bag) : null)

function codeOf(e: Bag): string | null {
	const detail = bagOf(e.detail)
	const payload = bagOf(e.payload)
	const c = detail?.code ?? e.code ?? payload?.code ?? payload?.error
	return typeof c === 'string' ? c : null
}

/** Ошибка api/client (status, detail, payload), тело {ok:false, error} или просто текст — про нехватку токенов? */
export function isInsufficientError(e: unknown): boolean {
	const bag = bagOf(e)
	if (!bag) return isInsufficientMessage(e)
	if (bag.status === 402) return true
	const code = codeOf(bag)
	if (code && INSUFFICIENT_CODES.has(code)) return true
	const detail = bagOf(bag.detail)
	return isInsufficientMessage(bag.message) || isInsufficientMessage(bag.error) || isInsufficientMessage(detail ? detail.message : bag.detail)
}

/** Суммы из ответа сервера, если он их прислал (у ручек разные имена полей). */
export function insufficientSums(e: unknown): { needed: number | null; balance: number | null } {
	const root = bagOf(e)
	const bags: Bag[] = []
	if (root) {
		bags.push(root)
		for (const k of ['detail', 'payload', 'details']) { const b = bagOf(root[k]); if (b) { bags.push(b); const bb = bagOf(b.details); if (bb) bags.push(bb) } }
	}
	const pick = (keys: string[]): number | null => {
		for (const b of bags) for (const k of keys) { const v = b[k]; const n = typeof v === 'string' ? Number(v) : v; if (typeof n === 'number' && Number.isFinite(n)) return n }
		return null
	}
	let needed = pick(NEEDED_KEYS)
	let balance = pick(BALANCE_KEYS)
	// Ручки без полей сумм пишут их в текст: «нужно 100, на балансе 33», «Нужно 12 токенов, у вас 3».
	if (needed == null || balance == null) {
		const texts = [root?.message, root?.error, bagOf(root?.detail)?.message, root?.detail, typeof e === 'string' ? e : null]
			.filter((t): t is string => typeof t === 'string').join(' ').toLowerCase()
		const n = /нужно (?:от )?(\d+(?:[.,]\d+)?)/.exec(texts)
		const b = /(?:на балансе|у вас|баланс)\s+(\d+(?:[.,]\d+)?)/.exec(texts)
		if (needed == null && n) needed = Number(n[1].replace(',', '.'))
		if (balance == null && b) balance = Number(b[1].replace(',', '.'))
	}
	return { needed, balance }
}

export const SHORT_FALLBACK = 'Не хватает токенов на этот запуск'

/** Единый текст отказа: с суммами сервера, если они есть. */
export function insufficientText(e: unknown, fallback: string = SHORT_FALLBACK): string {
	const { needed, balance } = insufficientSums(e)
	if (needed != null && balance != null) return shortTitle(needed, balance)
	if (needed != null) return `Не хватает токенов: нужно ${fmtTokens(needed)}`
	return fallback
}

// ── Реакция на отказ сервера: тост с «Пополнить» и свежий баланс. Обработчик задаёт AuthProvider. ──
let handler: ((text: string, silent: boolean) => void) | null = null
let reportedAt = 0

export function setInsufficientCreditsHandler(h: ((text: string, silent: boolean) => void) | null): void {
	handler = h
}

/** silent — только перечитать баланс, без тоста: место вызова показывает отказ само (чат — строкой с кнопкой). */
export function reportInsufficientCredits(text: string, opts: { silent?: boolean } = {}): void {
	reportedAt = Date.now()
	try { handler?.(text, !!opts.silent) } catch { /* тост — не причина ронять запрос */ }
}

/** Отказ уже показан стандартным тостом — свой тост места вызова о том же не нужен. */
export function insufficientReportedRecently(now: number = Date.now()): boolean {
	return now - reportedAt < 3000
}

// ── Переход к пополнению без перезагрузки: обычная ссылка + history (роутер слушает popstate) — работает и там,
// где нет контекста роутера (тост, шторки в тестах). ──
export const TOPUP_PATH = '/tariffs'

export function spaNavigate(to: string): void {
	window.history.pushState({}, '', to)
	window.dispatchEvent(new PopStateEvent('popstate'))
}

